import { useEffect, useMemo } from 'react';

import "@fontsource/audiowide";
import "@fontsource/bebas-neue";
import "@fontsource/covered-by-your-grace";
import "@fontsource/economica";
import "@fontsource/righteous";
import "@fontsource/russo-one";

import { InputNumber } from 'antd';
import DebugPrinter from 'components/Debug/DebugPrinter';
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import useFetchEntity from 'hooks/useFetchEntity';
import usePhaseIdTranslator from 'hooks/usePhaseIdTranslator';
import usePersistentStore from 'store/persistentStore';
import IG11PointLeadersCore from './Cores/IG11_PointLeaders_Core';
import BackgroundSelector from './Filters/BackgroundSelector';
import IgImgPlacement from './Filters/IgImgPlacement';
import IgImgSelector from './Filters/IgImgSelector';
import MarginEditor from './Filters/MarginEditor';
import PhaseSelector from './Filters/PhaseSelector';
import TeamMultiSelector from './Filters/TeamMultiSelector';
import { dateFormatIG } from './IGconfig';

const IG11_PointLeaders = ({ showData, exportRef, onMetadataChange }) => {
  const membership = usePersistentStore((state) => state.get('ig-membership'));
  const setMembership = usePersistentStore((state) => state.set('ig-membership'));
  const fgPictureUrl = usePersistentStore((state) => state.get('ig-pointleaders-fg-url'));
  const setFgPictureUrl = usePersistentStore((state) => state.set('ig-pointleaders-fg-url'));
  const size = usePersistentStore((state) => state.get('ig-pointleaders-size'));
  const setSize = usePersistentStore((state) => state.set('ig-pointleaders-size'));
  const SIZE_PRESETS = ['PORTRAIT', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 625, height: 1111, preset: 'PORTRAIT' };

  const fgImgPositionX = usePersistentStore((state) => state.get('ig-pointleaders-fgimgposition-x')) ?? 0;
  const setFgImgPositionX = usePersistentStore((state) => state.set('ig-pointleaders-fgimgposition-x'));
  const fgImgPositionY = usePersistentStore((state) => state.get('ig-pointleaders-fgimgposition-y')) ?? 0;
  const setFgImgPositionY = usePersistentStore((state) => state.set('ig-pointleaders-fgimgposition-y'));
  const fgImgZoom = usePersistentStore((state) => state.get('ig-pointleaders-fgzoom')) ?? 100;
  const setFgImgZoom = usePersistentStore((state) => state.set('ig-pointleaders-fgzoom'));

  const igname = 'pointleaders';
  const marginDefaults = { top: 0, left: 0, bottom: 0, right: 0 };
  const fgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setFgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));

  const selectedTeams = usePersistentStore((state) => state.get('ig-pointleaders-selected-teams')) ?? [];
  const setSelectedTeams = usePersistentStore((state) => state.set('ig-pointleaders-selected-teams'));
  const topCount = usePersistentStore((state) => state.get('ig-pointleaders-top-count'));
  const setTopCount = usePersistentStore((state) => state.set('ig-pointleaders-top-count'));

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-pointleaders-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-pointleaders-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-pointleaders-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-pointleaders-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-pointleaders-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-pointleaders-background-mode'));


  const { phaseIdsAll } = usePhaseIdTranslator(membership);
  const groupId = membership?.groupId;
  const seasonId = membership?.seasonId;

  const { data: groupDetail } = useFetchEntity('Group', groupId, 'Season>Competition');
  const { data: seasonDetail } = useFetchEntity('Season', seasonId, 'Competition');

  const metadata = useMemo(() => ({
    GroupName: groupDetail?.name,
    GroupColor: groupDetail?.color,
    SeasonName: seasonDetail?.name,
    CompetitionName: seasonDetail?.Competition?.name,
    CompetitionLogo:
      seasonDetail?.Competition?.logoUrl?.replace(
        "[size]",
        "cropped_md",
      ),
    Date: dayjs().format(dateFormatIG),
  }), [groupDetail?.name, groupDetail?.color, seasonDetail?.name, seasonDetail?.Competition?.name, seasonDetail?.Competition?.logoUrl]);

  // Notify parent component when metadata changes
  useEffect(() => {
    if (onMetadataChange && metadata.GroupName) {
      onMetadataChange(metadata);
    }
  }, [metadata, onMetadataChange]);

  return (
    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Membership:</span>
        <PhaseSelector
          value={membership}
          onChange={setMembership}
          mandatory={['competitionId', 'seasonId']}
        />

        <span>Foreground image:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrl} setFgPictureUrl={setFgPictureUrl} igId={11} />
        </div>

        <span className="relative top-[-14px]">Foreground placement:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionX}
            fgImgPositionY={fgImgPositionY}
            setFgImgPositionX={setFgImgPositionX}
            setFgImgPositionY={setFgImgPositionY}
            fgImgZoom={fgImgZoom}
            setFgImgZoom={setFgImgZoom}
          />
        </div>

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={11}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={fgImgMargin}
            setMargin={setFgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <span>Teams (optional):</span>
        <div className="flex gap-2 items-center">
          <TeamMultiSelector
            value={selectedTeams}
            onChange={setSelectedTeams}
          />
        </div>

        <span>Top (optional):</span>
        <div className="flex gap-2 items-center">
          <InputNumber
            min={1}
            max={100}
            value={topCount ?? undefined}
            onChange={(v) => setTopCount(v ?? undefined)}
            placeholder="No limit"
            style={{ width: 120 }}
          />
        </div>

        <div />
      </div>

      {showData && (<DebugPrinter data={[metadata]}></DebugPrinter>)}

      {phaseIdsAll != null &&
        <IG11PointLeadersCore
          exportRef={exportRef}
          metadata={metadata}
          size={size}
          fgImgPositionX={fgImgPositionX}
          fgImgPositionY={fgImgPositionY}
          setFgImgPositionX={setFgImgPositionX}
          setFgImgPositionY={setFgImgPositionY}
          fgImgZoom={fgImgZoom}
          setFgImgZoom={setFgImgZoom}
          fgPictureUrl={fgPictureUrl}
          fgImgMargin={fgImgMargin}
          backgroundMode={backgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          backgroundColor={backgroundColor}
          phaseIdsAll={phaseIdsAll}
          selectedTeams={selectedTeams}
          topCount={topCount}
          enablePositioning={true}
          useImageProxy={true}
        />
      }
    </>
  );
};

export default IG11_PointLeaders;
